// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The island index: the list of reporting cycles (list_reporting_cycles), plus the only place
// a cycle is CREATED (create_reporting_cycle, via NewReportingCycleDialog).
//
// The header row — and therefore "New cycle" — renders UNCONDITIONALLY, above the
// loading/error/empty branches. That ordering matters: the empty state is exactly the moment
// someone needs to create the first cycle, and the previous structure returned early from the
// whole component, so any control living below would have been unreachable precisely when it
// was most needed. The list BODY keeps the CioToday ordering (loading, then an unconditional
// error, then empty) unchanged.

import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { CheckCircle2, ChevronRight, Lock } from "lucide-react";
import { LoadingState, ErrorState, EmptyState } from "@/components/research-hub";
import {
  statusLabel,
  statusTone,
  toneDot,
  formatDay,
  NewReportingCycleDialog,
} from "@/components/investment-reporting";
import { useReportingAccess } from "@/hooks/investment-reporting/useReportingAccess";
import { useReportingCycles, type ReportingCycleListRow } from "@/hooks/investment-reporting/useReportingCycles";
import { REPORTING_BASE } from "./ReportingCycleLayout";

export default function ReportingCycleList() {
  const navigate = useNavigate();
  const access = useReportingAccess();
  const { data, isLoading, error, refetch } = useReportingCycles();
  // react-query v5 collapses `data` to `never` under this project's strictNullChecks:false;
  // alias to the known row type so the list logic below stays correctly typed.
  const cycles = (data ?? []) as ReportingCycleListRow[];

  // Anyone the server lets into this island may attempt to create a cycle. can_review_reporting()
  // is is_investment_team() OR is_operations_team() OR is_admin_or_superadmin(auth.uid()), which
  // is exactly the audience for this surface, so the client does not narrow it further.
  //
  // This previously read `access.canReview && !access.isAdmin`, which excluded admins and
  // superadmins from the create button. The justification given at the time was the
  // segregation-of-duties rule then shown in the layout banner ("Admin, read-only, cannot sign
  // the gate"). THAT RULE NO LONGER EXISTS: the CIO ruling of 2026-08-17 (migration 0003r) added
  // admin/superadmin to approve_reporting_gate's predicate and to the reporting_cycles gate write
  // guard, so admins may sign. The banner is gone with it.
  //
  // THE MISMATCH THAT USED TO BE NOTED HERE IS CLOSED. This comment previously recorded that the
  // button was offered to a role_level admin on neither team while create_reporting_cycle would
  // refuse them. Sam's first ruling of 2026-08-17 (migration 0003s) widened that RPC too;
  // measured live it now opens with
  //   IF NOT (is_investment_team() OR is_operations_team() OR is_admin_or_superadmin(auth.uid()))
  // so the button and the RPC agree exactly, for every account, not just for the seven that
  // happen to sit on a team today.
  const canCreate = access.canReview;

  // A brand-new cycle has no numbers yet, so send the creator to the drop point rather than
  // to an empty report.
  const openNewCycle = (cycleId: string) => navigate(`${REPORTING_BASE}/${cycleId}/intake`);

  const body = () => {
    // CioToday ordering: loading, then an UNCONDITIONAL error, then empty.
    if (isLoading && cycles.length === 0) return <LoadingState rows={3} />;
    if (error) return <ErrorState onRetry={() => refetch()} />;

    if (cycles.length === 0)
      return (
        <EmptyState
          title="No reporting cycles yet."
          body={
            canCreate
              ? "Create one for the period you're reporting — then drop that period's golden CSVs on its Intake tab."
              : "A cycle appears here once the Investment or Operations team creates one."
          }
        />
      );

    return (
      <div className="space-y-2">
        {cycles.map((c, i) => {
          const tone = statusTone(c.status);
          return (
            <Card
              key={c.id ?? i}
              className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => c.id && navigate(`${REPORTING_BASE}/${c.id}/report`)}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading text-base font-medium text-foreground">{c.period_label}</span>
                  <span className="inline-flex items-center gap-1.5 font-body text-xs">
                    <span className={cn("h-1.5 w-1.5 rounded-full", toneDot(tone))} aria-hidden />
                    {statusLabel(c.status)}
                  </span>
                  <span className="font-body text-[11px] rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
                    stage: {c.current_stage}
                  </span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-0.5">
                  As of {formatDay(c.as_of_date)} · {c.period_type}
                  {c.is_webinar_month ? " · webinar month" : ""}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {c.gate_approved ? (
                  <span className="inline-flex items-center gap-1 font-body text-xs text-primary">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Gate approved
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 font-body text-xs text-muted-foreground">
                    <Lock className="h-3.5 w-3.5" aria-hidden /> Awaiting sign-off
                  </span>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-base font-medium text-foreground">Cycles</h2>
        <NewReportingCycleDialog canCreate={canCreate} onCreated={openNewCycle} />
      </div>
      {body()}
    </div>
  );
}
